import React, { useEffect, useState } from 'react';
import { useCrudList } from '../../hooks/useCrudList';
import { apiRequest } from '../../utils/api';
import { MessageSetter } from '../../utils/messages';
import { UserRecord } from '../../types';

interface ManageUsersProps {
  userRole: 'admin' | 'manager' | 'user' | string;
  setMessage: MessageSetter;
}

interface UserFormState {
  email: string;
  password: string;
  isGoogleAccount: boolean;
  role: 'admin' | 'manager' | 'user';
  firstName: string;
  lastName: string;
}

const ManageUsers: React.FC<ManageUsersProps> = ({ userRole, setMessage }) => {
  const {
    items: users,
    loading: loadingUsers,
    fetchAll: fetchUsers,
    create: createUser,
    update: updateUser,
    remove: removeUser,
  } = useCrudList<UserRecord>({
    endpoint: '/api/users',
    useAuth: true,
    setMessage,
  });

  const [editingUser, setEditingUser] = useState<string | null>(null);
  const [userForm, setUserForm] = useState<UserFormState>({
    email: '',
    password: '',
    isGoogleAccount: false,
    role: 'user',
    firstName: '',
    lastName: '',
  });

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const resetForm = () => {
    setEditingUser(null);
    setUserForm({
      email: '',
      password: '',
      isGoogleAccount: false,
      role: 'user',
      firstName: '',
      lastName: '',
    });
  };

  const handleEditUser = (u: UserRecord) => {
    setEditingUser(u.id);
    setUserForm({
      email: u.email,
      password: '',
      isGoogleAccount: !!(u.isGoogleAccount) || !!u.googleId,
      role: u.role || 'user',
      firstName: u.firstName || '',
      lastName: u.lastName || '',
    });
  };

  const handleSaveUser = async (userId?: string) => {
    if (!userForm.email.trim()) {
      setMessage({ type: 'error', text: 'Email is required' });
      return;
    }

    if (!userId && !userForm.isGoogleAccount && !userForm.password.trim()) {
      setMessage({
        type: 'error',
        text: 'Password is required for new non-Google accounts',
      });
      return;
    }

    const payload: {
      email: string;
      isGoogleAccount: boolean;
      role: 'admin' | 'manager' | 'user';
      firstName: string | null;
      lastName: string | null;
      password?: string;
    } = {
      email: userForm.email.trim(),
      isGoogleAccount: userForm.isGoogleAccount,
      role: userForm.role,
      firstName: userForm.firstName.trim() || null,
      lastName: userForm.lastName.trim() || null,
    };

    try {
      if (userId) {
        // Only send password if set for updates
        if (userForm.password.trim()) {
          payload.password = userForm.password;
        }
        await updateUser(userId, payload, 'User updated successfully');
      } else {
        if (!userForm.isGoogleAccount) {
          payload.password = userForm.password;
        }
        await createUser(payload, 'User created successfully');
      }
      resetForm();
    } catch (error) {
      console.error('Error saving user:', error);
      // apiRequest / useCrudList already set the message
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await removeUser(userId, 'User deleted successfully');
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const handleResetPassword = async (userId: string) => {
    const newPassword = window.prompt('Enter new password for this user:');
    if (!newPassword || !newPassword.trim()) return;

    try {
      await apiRequest(
        `/api/users/${userId}`,
        {
          method: 'PUT',
          body: JSON.stringify({ password: newPassword }),
        },
        {
          useAuth: true,
          setMessage,
          successMessage: 'Password reset successfully',
        }
      );
    } catch (error) {
      console.error('Error resetting password:', error);
    }
  };

  return (
    <div className="card">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: '1.5rem',
            color: 'var(--text-primary)',
          }}
        >
          Manage Users
        </h2>
        <button
          className="btn btn-primary"
          onClick={() => {
            setEditingUser('new');
            setUserForm({
              email: '',
              password: '',
              isGoogleAccount: false,
              role: 'user',
              firstName: '',
              lastName: '',
            });
          }}
          disabled={editingUser !== null && editingUser !== 'new'}
        >
          ➕ Add User
        </button>
      </div>

      {editingUser === 'new' && (
        <div
          className="card"
          style={{
            padding: '1.5rem',
            backgroundColor: 'var(--surface)',
            marginBottom: '1.5rem',
          }}
        >
          <h3
            style={{
              margin: '0 0 1rem 0',
              fontSize: '1.1rem',
              color: 'var(--text-primary)',
            }}
          >
            Add New User
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                  }}
                >
                  First Name *
                </label>
                <input
                  type="text"
                  value={userForm.firstName}
                  onChange={(e) =>
                    setUserForm({
                      ...userForm,
                      firstName: e.target.value,
                    })
                  }
                  placeholder="First Name"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    fontSize: '0.875rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                  }}
                >
                  Last Name *
                </label>
                <input
                  type="text"
                  value={userForm.lastName}
                  onChange={(e) =>
                    setUserForm({
                      ...userForm,
                      lastName: e.target.value,
                    })
                  }
                  placeholder="Last Name"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    fontSize: '0.875rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                />
              </div>
            </div>
            <div>
              <label
                style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontSize: '0.875rem',
                  color: 'var(--text-secondary)',
                }}
              >
                Email *
              </label>
              <input
                type="email"
                value={userForm.email}
                onChange={(e) =>
                  setUserForm({
                    ...userForm,
                    email: e.target.value,
                  })
                }
                placeholder="email@example.com"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  fontSize: '0.875rem',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                }}
              />
            </div>
            <div>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  marginBottom: '0.5rem',
                  fontSize: '0.875rem',
                  color: 'var(--text-secondary)',
                }}
              >
                <input
                  type="checkbox"
                  checked={userForm.isGoogleAccount}
                  onChange={(e) =>
                    setUserForm({
                      ...userForm,
                      isGoogleAccount: e.target.checked,
                      password: '',
                    })
                  }
                />
                Google Account (no password needed)
              </label>
            </div>
            {!userForm.isGoogleAccount && (
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                  }}
                >
                  Password *
                </label>
                <input
                  type="password"
                  value={userForm.password}
                  onChange={(e) =>
                    setUserForm({
                      ...userForm,
                      password: e.target.value,
                    })
                  }
                  placeholder="Password"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    fontSize: '0.875rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                />
              </div>
            )}
            {userRole === 'admin' && (
              <div>
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    fontSize: '0.875rem',
                    color: 'var(--text-secondary)',
                  }}
                >
                  Role
                </label>
                <select
                  value={userForm.role}
                  onChange={(e) =>
                    setUserForm({
                      ...userForm,
                      role: e.target.value as 'admin' | 'manager' | 'user',
                    })
                  }
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    fontSize: '0.875rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                  }}
                >
                  <option value="user">User</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                className="btn btn-primary"
                onClick={() => handleSaveUser()}
              >
                Save User
              </button>
              <button
                className="btn btn-secondary"
                onClick={resetForm}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {loadingUsers ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>Loading users...</div>
      ) : users.length === 0 && editingUser !== 'new' ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👥</div>
          <h3
            style={{
              color: 'var(--text-secondary)',
              marginBottom: '0.5rem',
            }}
          >
            No users found
          </h3>
          <p style={{ color: 'var(--text-muted)' }}>Add users to get started.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {users.map((u) => (
            <div
              key={u.id}
              className="card"
              style={{
                padding: '1rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {editingUser === u.id ? (
                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.75rem',
                  }}
                >
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '0.75rem',
                    }}
                  >
                    <input
                      type="text"
                      value={userForm.firstName}
                      onChange={(e) =>
                        setUserForm({
                          ...userForm,
                          firstName: e.target.value,
                        })
                      }
                      placeholder="First Name"
                      style={{
                        padding: '0.5rem',
                        fontSize: '0.875rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                      }}
                    />
                    <input
                      type="text"
                      value={userForm.lastName}
                      onChange={(e) =>
                        setUserForm({
                          ...userForm,
                          lastName: e.target.value,
                        })
                      }
                      placeholder="Last Name"
                      style={{
                        padding: '0.5rem',
                        fontSize: '0.875rem',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                      }}
                    />
                  </div>
                  <input
                    type="email"
                    value={userForm.email}
                    onChange={(e) =>
                      setUserForm({
                        ...userForm,
                        email: e.target.value,
                      })
                    }
                    placeholder="Email"
                    style={{
                      padding: '0.5rem',
                      fontSize: '0.875rem',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                    }}
                  />
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.875rem',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={userForm.isGoogleAccount}
                      onChange={(e) =>
                        setUserForm({
                          ...userForm,
                          isGoogleAccount: e.target.checked,
                          password: '',
                        })
                      }
                    />
                    Google Account
                  </label>
                  {userRole === 'admin' && (
                    <div>
                      <label
                        style={{
                          display: 'block',
                          marginBottom: '0.5rem',
                          fontSize: '0.875rem',
                          color: 'var(--text-secondary)',
                        }}
                      >
                        Role
                      </label>
                      <select
                        value={userForm.role}
                        onChange={(e) =>
                          setUserForm({
                            ...userForm,
                            role: e.target.value as 'admin' | 'manager' | 'user',
                          })
                        }
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          fontSize: '0.875rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                        }}
                      >
                        <option value="user">User</option>
                        <option value="manager">Manager</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      className="btn btn-primary"
                      onClick={() => handleSaveUser(u.id)}
                      style={{
                        fontSize: '0.875rem',
                        padding: '0.5rem 1rem',
                      }}
                    >
                      Save
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={resetForm}
                      style={{
                        fontSize: '0.875rem',
                        padding: '0.5rem 1rem',
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        color: 'var(--text-primary)',
                        marginBottom: '0.25rem',
                      }}
                    >
                      {u.firstName && u.lastName
                        ? `${u.firstName} ${u.lastName}`
                        : u.firstName || u.lastName || u.email.split('@')[0]}
                    </div>
                    <div
                      style={{
                        fontSize: '0.75rem',
                        color: 'var(--text-secondary)',
                        marginBottom: '0.25rem',
                      }}
                      className="email-account-string"
                    >
                      {u.email}
                    </div>
                    <div className="flex gap-sm items-center">
                      {u.isGoogleAccount || u.googleId ? (
                        <span className="badge badge-cyan">Google Account</span>
                      ) : (
                        <span
                          className="badge"
                          style={{
                            backgroundColor: 'var(--surface-alt)',
                            color: 'var(--text-secondary)',
                            border: '1px solid var(--grid-line)',
                          }}
                        >
                          Password Login
                        </span>
                      )}
                      <span
                        className={`badge ${
                          u.role === 'admin'
                            ? 'badge-danger'
                            : u.role === 'manager'
                            ? 'badge-warning'
                            : 'badge-success'
                        }`}
                      >
                        {u.role || 'user'}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {userRole === 'admin' && (
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleEditUser(u)}
                        style={{
                          fontSize: '0.875rem',
                          padding: '0.5rem 1rem',
                        }}
                      >
                        Edit
                      </button>
                    )}
                    {userRole === 'admin' && !u.isGoogleAccount && !u.googleId && (
                      <button
                        className="btn"
                        onClick={() => handleResetPassword(u.id)}
                        style={{
                          fontSize: '0.875rem',
                          padding: '0.5rem 1rem',
                          backgroundColor: 'var(--warning)',
                          color: 'var(--void)',
                        }}
                      >
                        🔑 Reset Password
                      </button>
                    )}
                    {userRole === 'admin' && (
                      <button
                        className="btn btn-secondary"
                        onClick={() => handleDeleteUser(u.id)}
                        style={{
                          fontSize: '0.875rem',
                          padding: '0.5rem 1rem',
                          backgroundColor: '#e74c3c',
                          color: 'white',
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ManageUsers;
